import { AfterViewInit, Component, Input, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormArray, FormControl, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { Address, countryDefaultValue } from "@models/address/address";
import { EntityChangeData } from "@models/documents/entity-change-data";
import { Relationship } from "@models/relationships/relationship";
import { RelationshipType } from "@models/relationships/relationship-type.enum";
import { IndividualCorporateRelationship } from "@models/trusts/trust-change-relationship";
import { TrustSubType, TrustType, trustTypeBySubtype } from "@models/trusts/trusts-enums";
import { Guid } from "@shared/utils/guid.helper";
import { setControlDisabled } from "@shared/utils/set-control-disabled";
import { CustomFormValidators } from "@shared/validators/custom-form-validators";
import {
  CorporateRelationshipFormGroupComponent
} from "../../../reusable-form-groups/corporate-relationship-form-group/corporate-relationship-form-group.component";
import {
  IndividualRelationshipFormGroupComponent
} from "../../../reusable-form-groups/relationship-form-group/individual-relationship-form-group.component";
import {
  AuthorisedSignatoriesStepComponent
} from "../../../reusable-form-parts/authorised-signatories-step/authorised-signatories-step.component";
import { BaseStepperFormComponent } from "../../stepper-form";
import {
  trustEstablishmentFormKeys,
  trustEstablishmentHeaders,
  trustEstablishmentSubheaders
} from "../constants/trust-establishment-texts";
import {
  IIndividualCorporateRelationshipFormControls,
  IIndividualCorporateRelationshipFormGroup
} from "../trust-discretionary-establishment/trust-discretionary-establishment.component";

@Component({
  selector: 'app-base-trust-establishment-form',
  template: '',
  standalone: true
})
export class BaseTrustEstablishmentFormComponent<T, Change extends EntityChangeData> extends BaseStepperFormComponent<T, Change> implements OnInit, AfterViewInit {
  @Input() trustType!: TrustType;
  @Input() trustSubtype!: TrustSubType;

  readonly maxIndividualTrusteeAppointorsToAdd = 4;
  readonly maxCorporateTrusteeToAdd = 1;
  readonly authSignatoriesValidators: ValidatorFn[] = [AuthorisedSignatoriesStepComponent.maxSignatoriesLimitValidator(2)];

  header = 'Trust Establishment';
  subheader = '';
  formIcon = 'ic-form-20';
  formKey = '';

  documentId = Guid.EmptyGuid;
  entityId = Guid.EmptyGuid;

  ngOnInit(): void {
    this.setFormTexts();
    this.setCurrentStep(0);
  }

  setFormTexts(trustSubtype = this.trustSubtype): void {
    this.trustSubtype = trustSubtype;
    this.trustType = trustTypeBySubtype[trustSubtype];
    this.header = trustEstablishmentHeaders[trustSubtype];
    this.subheader = trustEstablishmentSubheaders[trustSubtype];
    this.formKey = trustEstablishmentFormKeys[trustSubtype];
    this.formIcon = 'ic-form-20';
  }

  updateFormSteps(): void {}

  createIndividualCorporateRelationshipFormGroup(trustee: Partial<Relationship> = {}): IIndividualCorporateRelationshipFormGroup {
    const trusteeTypeValue = trustee.entityDataOverride ? IndividualCorporateRelationship.Corporate : IndividualCorporateRelationship.Individual;

    const trusteeType = new FormControl<IndividualCorporateRelationship | null>(null, [Validators.required, CustomFormValidators.maxLength(50)]);
    const individualTrustee = IndividualRelationshipFormGroupComponent.defineForm();
    const corporateTrustee = CorporateRelationshipFormGroupComponent.defineForm();

    trusteeType.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((trusteeType) => {
        setControlDisabled(corporateTrustee, trusteeType !== IndividualCorporateRelationship.Corporate);
        setControlDisabled(individualTrustee, trusteeType !== IndividualCorporateRelationship.Individual);
      });

    if (trusteeTypeValue == IndividualCorporateRelationship.Individual && trustee.individualDataOverride) {
      const individual = trustee.individualDataOverride;

      individualTrustee.patchValue({
        name: individual.fullName,
        address: individual.address
      });
    }

    if (trusteeTypeValue == IndividualCorporateRelationship.Corporate && trustee.entityDataOverride) {
      const corporate = trustee.entityDataOverride;

      corporateTrustee.patchValue({
        name: corporate.name ?? '',
        acn: corporate.entityNumber ?? null,
        address: corporate.registeredAddress ?? new Address({ country: countryDefaultValue }),
        isOverseasCompany: false
      });
    }

    if (trustee.individualDataOverride || trustee.entityDataOverride) {
      trusteeType.setValue(trusteeTypeValue);
    }

    return new FormGroup<IIndividualCorporateRelationshipFormControls>({
      individualId: new FormControl<string | null>(trustee?.individualId || Guid.generate()),
      trusteeType,
      corporateTrustee,
      individualTrustee,
      types: new FormControl<RelationshipType[]>([trustee?.type ?? RelationshipType.Trustee], { nonNullable: true }),
      relationshipId: new FormControl<string | null>(trustee.relationshipId || Guid.generate())
    });
  }

  createTrusteeFormGroup(trustee: Partial<Relationship> = {}, listenFirstTrusteeChanges = false): IIndividualCorporateRelationshipFormGroup {
    const form = this.createIndividualCorporateRelationshipFormGroup(trustee);

    if (listenFirstTrusteeChanges) {
      this.listenFirstTrusteeTrusteeTypeChanges(form);
    } else {
      form.controls.trusteeType.setValue(IndividualCorporateRelationship.Individual);
      setControlDisabled(form.controls.trusteeType);
    }

    return form;
  }

  listenFirstTrusteeTrusteeTypeChanges(trusteeFormGroup: IIndividualCorporateRelationshipFormGroup): void {
    trusteeFormGroup.controls.trusteeType.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((trusteeType) => this.disableTrusteeTrusteeTypeControls(trusteeType));
  }

  disableTrusteeTrusteeTypeControls(firstTrusteeTrusteeType: IndividualCorporateRelationship | null): void {
    if (firstTrusteeTrusteeType == IndividualCorporateRelationship.Individual) {
      // Disabled every trustee TrusteeType after first trustee

      this.trusteesFormArray.controls
        .forEach((trusteeFormGroup, index) => {
          if (index < 1) return;

          setControlDisabled(trusteeFormGroup.controls.trusteeType);
        });
    } else if (firstTrusteeTrusteeType == IndividualCorporateRelationship.Corporate) {
      // Remove all trustee after first

      while (this.trusteesFormArray.controls.length > 1) {
        this.removeTrustee(1);
      }

      const firstTrusteeFormGroup = this.trusteesFormArray.at(0);
      firstTrusteeFormGroup.controls.types.setValue([RelationshipType.Trustee]);
    }

    this.updateFormSteps();
  }

  addTrustee(): void {
    const trusteeForm = this.createTrusteeFormGroup({ type: RelationshipType.Trustee }, this.trusteesFormArray.controls.length == 0);
    this.trusteesFormArray.push(trusteeForm);
  }

  removeTrustee(index: number): void {
    this.trusteesFormArray.removeAt(index);
  }

  get trusteesFormArray(): FormArray<IIndividualCorporateRelationshipFormGroup> {
    throw new Error('trusteesFormArray is not defined');
  }

  get isAddTrusteeButtonHidden(): boolean {
    return this.trusteesFormArray.at(0)?.controls?.trusteeType?.value == IndividualCorporateRelationship.Corporate && this.trusteesFormArray.controls.length >= this.maxCorporateTrusteeToAdd;
  }

  get isAddTrusteeButtonDisabled(): boolean {
    return this.isAddTrusteeButtonHidden || this.trusteesFormArray.controls.length >= this.maxIndividualTrusteeAppointorsToAdd;
  }
}
